import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import enMessages from '@cloudscape-design/components/i18n/messages/all.en';
import { I18nProvider } from '@cloudscape-design/components/i18n';
import { QueryParamProvider } from 'use-query-params';
import queryString from 'query-string';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useState } from 'react';
import { NavigationContext } from '../common/navigation';
import ErrorPage from '../error';
import { liveApiUrl } from '../api/client';
import { getErrorDetails } from '../components/Loading';
import { redirectToManage } from '../auth/useDistributorAuth';
import { redirectToLogin } from '../auth/useAuth';
import { TanStackRouterAdapter } from '../common/query-param-adapter';
import { getSentryErrorTags, shouldReportToSentry } from '../common/api/errors';

import '@cloudscape-design/global-styles/index.css';
import '../global.css';

export function isProductionSentryHost(hostname: string): boolean {
  return hostname === 'scorecard.realtimefeedback.com';
}

const AUTOMATION_BREADCRUMB = 'antifingerprint not defined yet';

export function filterAutomationNoise(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  const isKnownAutomation = event.breadcrumbs?.some((breadcrumb) =>
    breadcrumb.message?.includes(AUTOMATION_BREADCRUMB),
  );
  const isInjectedScriptError = event.exception?.values?.some((exception) => {
    const frames = exception.stacktrace?.frames;
    return exception.value === "Right-hand side of 'instanceof' is not an object" &&
      frames?.some((frame) => frame.function?.startsWith('tmsa.')) &&
      frames.every((frame) => frame.filename === '<anonymous>');
  });
  return isKnownAutomation || isInjectedScriptError ? null : event;
}

Sentry.init({
  dsn: 'https://3d4f2949823500d9e9b25b3d142771a8@o4507625924722688.ingest.us.sentry.io/4508846988066816',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
    }),
  ],
  tracesSampleRate: import.meta.env.DEV ? 0.0 : 0.05,
  tracePropagationTargets: ['localhost', liveApiUrl],
  profilesSampleRate: 1.0,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: import.meta.env.DEV ? 0.0 : 1.0,
  environment: import.meta.env.DEV ? 'development' : 'production',
  release: import.meta.env.VITE_SENTRY_RELEASE,
  beforeSend: filterAutomationNoise,
  enabled: isProductionSentryHost(window.location.hostname),
});

function captureQueryError(
  error: unknown,
  operation: 'query' | 'mutation',
  key: readonly unknown[] | undefined,
  ignoreNetworkErrors = false,
): void {
  if (!shouldReportToSentry(error, { ignoreNetworkErrors })) return;

  const errorValue = error instanceof Error ? error : new Error(String(error));
  const { title, description } = getErrorDetails(errorValue);
  Sentry.captureException(errorValue, {
    tags: {
      operation,
      queryFamily: String(key?.[0] ?? 'unkeyed'),
      ...getSentryErrorTags(error),
    },
    extra: {
      title,
      description,
      queryKey: key,
      route: window.location.pathname,
      online: navigator.onLine,
      visibility: document.visibilityState,
    },
  });
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: ErrorPage,
});

function AppShell() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash !== '') {
      setTimeout(() => {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    } else if (!location.searchStr) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <Outlet />
    </Sentry.ErrorBoundary>
  );
}

function RootComponent() {
  const { innerWidth: width } = window;
  const navigationSize = 200;
  const [navigationOpen, setNavigationOpen] = useState(width > 688);

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
      queryCache: new QueryCache({
        onError: async (error, query) => {
          if (error instanceof Error && error.message.includes('401')) {
            const path = window.location.pathname;
            if (path.startsWith('/dashboard')) {
              redirectToLogin();
              return;
            }
            if (path.startsWith('/distributor') || path.startsWith('/auth/callback')) {
              redirectToManage();
              return;
            }
          }
          captureQueryError(error, 'query', query.queryKey);
        },
      }),
      mutationCache: new MutationCache({
        onError: async (error, _variables, _context, mutation) => {
          if (error instanceof Error && error.message.includes('401')) {
            const path = window.location.pathname;
            if (path.startsWith('/dashboard')) {
              redirectToLogin();
              return;
            }
            if (path.startsWith('/distributor') || path.startsWith('/auth/callback')) {
              redirectToManage();
              return;
            }
          }
          captureQueryError(
            error,
            'mutation',
            mutation.options.mutationKey,
            mutation.options.meta?.ignoreNetworkErrors === true,
          );
        },
      }),
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <QueryParamProvider
        adapter={TanStackRouterAdapter}
        options={{
          searchStringToObject: queryString.parse,
          objectToSearchString: queryString.stringify,
          removeDefaultsFromUrl: true,
        }}
      >
        <I18nProvider locale="en" messages={[enMessages]}>
          <NavigationContext.Provider value={{ navigationOpen, setNavigationOpen, navigationSize }}>
            <AppShell />
          </NavigationContext.Provider>
        </I18nProvider>
      </QueryParamProvider>
      {import.meta.env.DEV && <TanStackRouterDevtools />}
    </QueryClientProvider>
  );
}
